import React, { useRef, useState } from "react";
import { api, API, getToken } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "./ui/dialog";
import { GitCompare, UploadCloud, Loader2, Plus, Minus, Pencil, FileSearch } from "lucide-react";
import { toast } from "sonner";

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function CTARescanDiff({ cta, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("upload"); // upload | diff
  const [busy, setBusy] = useState(false);
  const [diff, setDiff] = useState(null);
  const [saved, setSaved] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [picks, setPicks] = useState({});
  const inputRef = useRef();

  const reset = () => {
    setStep("upload"); setDiff(null); setSaved(null); setExtracted(null); setPicks({}); setBusy(false);
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await fetch(`${API}/ctas/${cta.id}/rescan-and-diff`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Re-scan failed");
      setSaved(data.saved); setExtracted(data.extracted); setDiff(data.diff);
      // Default: tick everything
      const initial = {};
      data.diff.metadata_changes.forEach((c, i) => (initial[`meta-${i}`] = true));
      data.diff.invoicables.added.forEach((_, i) => (initial[`inv-add-${i}`] = true));
      data.diff.invoicables.removed.forEach((_, i) => (initial[`inv-rem-${i}`] = false)); // default OFF for removals
      data.diff.invoicables.changed.forEach((_, i) => (initial[`inv-chg-${i}`] = true));
      data.diff.visit_grants.added.forEach((_, i) => (initial[`v-add-${i}`] = true));
      data.diff.visit_grants.removed.forEach((_, i) => (initial[`v-rem-${i}`] = false));
      data.diff.visit_grants.matched.forEach((m, i) => {
        initial[`v-chg-${i}`] = m.changes.length > 0;
        m.activities.added.forEach((_, j) => (initial[`v${i}-aa-${j}`] = true));
        m.activities.removed.forEach((_, j) => (initial[`v${i}-ar-${j}`] = false));
        m.activities.changed.forEach((_, j) => (initial[`v${i}-ac-${j}`] = true));
      });
      data.diff.pass_throughs.added.forEach((_, i) => (initial[`pt-add-${i}`] = true));
      data.diff.pass_throughs.removed.forEach((_, i) => (initial[`pt-rem-${i}`] = false));
      data.diff.pass_throughs.changed.forEach((_, i) => (initial[`pt-chg-${i}`] = true));
      setPicks(initial);
      setStep("diff");
      const s = data.diff.summary;
      if (s.total_changes === 0) {
        toast.info("No differences detected — saved CTA matches the document.");
      } else {
        toast.success(`Found ${s.total_changes} change${s.total_changes !== 1 ? "s" : ""}`);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const tick = (k, v) => setPicks((p) => ({ ...p, [k]: v }));

  const apply = async () => {
    if (!diff) return;
    setBusy(true);
    try {
      // Start from saved CTA shape
      const merged = { ...saved };

      // Metadata
      diff.metadata_changes.forEach((c, i) => {
        if (picks[`meta-${i}`]) merged[c.field] = c.new;
      });

      // Invoicables: build new array
      const invByKey = (i) => `${(i.name || "").toLowerCase().trim()}|${(i.category || "other").toLowerCase()}`;
      const removedInvKeys = new Set(
        diff.invoicables.removed.filter((_, i) => picks[`inv-rem-${i}`]).map(invByKey)
      );
      const changedInvMap = {};
      diff.invoicables.changed.forEach((c, i) => {
        if (picks[`inv-chg-${i}`]) changedInvMap[invByKey(c.existing)] = c.new;
      });
      let nextInv = (saved.invoicables || [])
        .filter((i) => !removedInvKeys.has(invByKey(i)))
        .map((i) => changedInvMap[invByKey(i)] || i);
      diff.invoicables.added.forEach((a, i) => { if (picks[`inv-add-${i}`]) nextInv.push(a); });
      merged.invoicables = nextInv;

      // Pass-throughs
      const ptKey = (p) => (p.name || "").toLowerCase().trim();
      const removedPtKeys = new Set(diff.pass_throughs.removed.filter((_, i) => picks[`pt-rem-${i}`]).map(ptKey));
      const changedPtMap = {};
      diff.pass_throughs.changed.forEach((c, i) => {
        if (picks[`pt-chg-${i}`]) changedPtMap[ptKey(c.existing)] = c.new;
      });
      let nextPt = (saved.pass_throughs || [])
        .filter((p) => !removedPtKeys.has(ptKey(p)))
        .map((p) => changedPtMap[ptKey(p)] || p);
      diff.pass_throughs.added.forEach((a, i) => { if (picks[`pt-add-${i}`]) nextPt.push(a); });
      merged.pass_throughs = nextPt;

      // Visits
      const vKey = (v) => (v.visit_code || v.visit_name || "").toLowerCase().trim();
      const removedVKeys = new Set(diff.visit_grants.removed.filter((_, i) => picks[`v-rem-${i}`]).map(vKey));
      let nextVisits = (saved.visit_grants || []).filter((v) => !removedVKeys.has(vKey(v)));

      // Apply matched visit changes (top-level + activities sub-diff)
      diff.visit_grants.matched.forEach((m, mi) => {
        const idx = nextVisits.findIndex((v) => vKey(v) === vKey(m.existing));
        if (idx < 0) return;
        const sv = { ...nextVisits[idx] };
        // Apply top-level field changes
        if (picks[`v-chg-${mi}`]) {
          m.changes.forEach((c) => { sv[c.field] = c.new; });
        }
        // Activities: build new array
        const aKey = (a) => (a.name || "").toLowerCase().trim();
        const remA = new Set(m.activities.removed.filter((_, j) => picks[`v${mi}-ar-${j}`]).map(aKey));
        const chgAMap = {};
        m.activities.changed.forEach((c, j) => {
          if (picks[`v${mi}-ac-${j}`]) chgAMap[aKey(c.existing)] = c.new;
        });
        let nextActs = (sv.activities || [])
          .filter((a) => !remA.has(aKey(a)))
          .map((a) => chgAMap[aKey(a)] || a);
        m.activities.added.forEach((a, j) => { if (picks[`v${mi}-aa-${j}`]) nextActs.push(a); });
        sv.activities = nextActs;
        nextVisits[idx] = sv;
      });

      // Add new visits
      diff.visit_grants.added.forEach((a, i) => { if (picks[`v-add-${i}`]) nextVisits.push(a); });
      merged.visit_grants = nextVisits;

      // Save via PUT — strip server-managed fields
      const payload = { ...merged };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      await api.put(`/ctas/${cta.id}`, payload);
      toast.success("CTA updated");
      setOpen(false);
      reset();
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const Section = ({ title, count, children }) => (
    <div className="border border-slate-200 rounded-md overflow-hidden">
      <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-[12px] font-semibold flex items-center gap-2">
        {title} <Badge variant="outline">{count}</Badge>
      </div>
      <div className="p-3 space-y-1.5">{children}</div>
    </div>
  );

  const Row = ({ k, icon, color, children }) => (
    <label className="flex items-start gap-2 text-[12px] cursor-pointer hover:bg-slate-50 rounded px-1.5 py-1">
      <Checkbox checked={!!picks[k]} onCheckedChange={(v) => tick(k, !!v)} className="mt-0.5" data-testid={`diff-${k}`} />
      <span className={`mt-0.5 ${color}`}>{icon}</span>
      <span className="flex-1">{children}</span>
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button size="icon" variant="ghost" title="Re-scan original document & diff" data-testid={`rescan-diff-btn-${cta.id}`}>
          <FileSearch className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="w-5 h-5 text-[#0A4C95]" /> Re-scan & Diff · {cta.study_name}
          </DialogTitle>
        </DialogHeader>

        {step === "upload" && (
          <div className="py-4">
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-10 text-center hover:border-[#0A4C95] transition-colors cursor-pointer"
                 onClick={() => inputRef.current?.click()}
                 data-testid="diff-upload-drop">
              {busy ? (
                <>
                  <Loader2 className="w-10 h-10 mx-auto text-[#0A4C95] animate-spin" />
                  <div className="mt-3 text-sm font-semibold">Re-extracting and diffing...</div>
                </>
              ) : (
                <>
                  <UploadCloud className="w-10 h-10 mx-auto text-slate-400" />
                  <div className="mt-3 text-sm font-semibold">Upload the latest version of the CTA document</div>
                  <div className="text-xs text-slate-500 mt-1">PDF · DOCX · XLSX · TXT</div>
                  <Button className="mt-4 bg-[#0A4C95] hover:bg-[#083d79]">Choose File</Button>
                </>
              )}
              <input ref={inputRef} type="file" hidden accept=".pdf,.docx,.xlsx,.xlsm,.txt" onChange={upload} data-testid="diff-file-input" />
            </div>
            <div className="mt-4 text-xs text-slate-500 bg-blue-50 border border-blue-200 rounded p-3">
              Useful when the sponsor sends a revised budget exhibit. We'll compare every line and let you pick which changes to apply.
            </div>
          </div>
        )}

        {step === "diff" && diff && (
          <div className="py-2 space-y-3" data-testid="diff-review">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <SummaryStat label="Metadata" v={diff.summary.metadata_changes} />
              <SummaryStat label="Invoicables Δ" v={diff.summary.invoicables_added + diff.summary.invoicables_removed + diff.summary.invoicables_changed} />
              <SummaryStat label="Visits Δ" v={diff.summary.visits_added + diff.summary.visits_removed + diff.summary.visits_changed} />
              <SummaryStat label="Total Changes" v={diff.summary.total_changes} highlight />
            </div>

            {diff.metadata_changes.length > 0 && (
              <Section title="Metadata Changes" count={diff.metadata_changes.length}>
                {diff.metadata_changes.map((c, i) => (
                  <Row key={`meta-${c.field}`} k={`meta-${i}`} icon={<Pencil className="w-3 h-3" />} color="text-amber-600">
                    <strong>{c.field}</strong>: <span className="text-slate-500 line-through">{String(c.old || "—")}</span> → <span className="text-emerald-700 font-semibold">{String(c.new || "—")}</span>
                  </Row>
                ))}
              </Section>
            )}

            {(diff.invoicables.added.length + diff.invoicables.removed.length + diff.invoicables.changed.length) > 0 && (
              <Section title="Invoicables" count={diff.invoicables.added.length + diff.invoicables.removed.length + diff.invoicables.changed.length}>
                {diff.invoicables.added.map((i, idx) => (
                  <Row key={`add-${idx}`} k={`inv-add-${idx}`} icon={<Plus className="w-3 h-3" />} color="text-emerald-600">
                    Add <strong>{i.name}</strong> [{i.category}] · {fmtUSD(i.amount)}
                  </Row>
                ))}
                {diff.invoicables.changed.map((c, idx) => (
                  <Row key={`chg-${idx}`} k={`inv-chg-${idx}`} icon={<Pencil className="w-3 h-3" />} color="text-amber-600">
                    Update <strong>{c.existing.name}</strong>: {c.changes.map((ch) => `${ch.field} ${String(ch.old)} → ${String(ch.new)}`).join("; ")}
                  </Row>
                ))}
                {diff.invoicables.removed.map((i, idx) => (
                  <Row key={`rem-${idx}`} k={`inv-rem-${idx}`} icon={<Minus className="w-3 h-3" />} color="text-red-600">
                    Remove <strong>{i.name}</strong> [{i.category}] · {fmtUSD(i.amount)} <span className="text-slate-400">(only saved, not in new doc)</span>
                  </Row>
                ))}
              </Section>
            )}

            {(diff.visit_grants.added.length + diff.visit_grants.removed.length + diff.visit_grants.matched.length) > 0 && (
              <Section title="Visits & Activities" count={diff.summary.visits_added + diff.summary.visits_removed + diff.summary.visits_changed}>
                {diff.visit_grants.added.map((v, idx) => (
                  <Row key={`v-add-${idx}`} k={`v-add-${idx}`} icon={<Plus className="w-3 h-3" />} color="text-emerald-600">
                    Add visit <strong>{v.visit_code} {v.visit_name}</strong> · {fmtUSD(v.grant_amount)} ({(v.activities || []).length} activities)
                  </Row>
                ))}
                {diff.visit_grants.removed.map((v, idx) => (
                  <Row key={`v-rem-${idx}`} k={`v-rem-${idx}`} icon={<Minus className="w-3 h-3" />} color="text-red-600">
                    Remove visit <strong>{v.visit_code} {v.visit_name}</strong> · {fmtUSD(v.grant_amount)}
                  </Row>
                ))}
                {diff.visit_grants.matched.map((m, mi) => (
                  <div key={`v-mat-${mi}`} className="border-l-2 border-slate-200 pl-3 mt-2">
                    <div className="text-[11.5px] font-semibold text-slate-700">{m.visit_code} {m.visit_name}</div>
                    {m.changes.length > 0 && (
                      <Row k={`v-chg-${mi}`} icon={<Pencil className="w-3 h-3" />} color="text-amber-600">
                        Update visit fields: {m.changes.map((c) => `${c.field} ${String(c.old)} → ${String(c.new)}`).join("; ")}
                      </Row>
                    )}
                    {m.activities.added.map((a, ai) => (
                      <Row key={`aa-${mi}-${ai}`} k={`v${mi}-aa-${ai}`} icon={<Plus className="w-3 h-3" />} color="text-emerald-600">
                        + activity <strong>{a.name}</strong> · {fmtUSD(a.amount)}
                      </Row>
                    ))}
                    {m.activities.changed.map((c, ai) => (
                      <Row key={`ac-${mi}-${ai}`} k={`v${mi}-ac-${ai}`} icon={<Pencil className="w-3 h-3" />} color="text-amber-600">
                        Δ activity <strong>{c.existing.name}</strong>: {c.changes.map((ch) => `${ch.field} ${String(ch.old)} → ${String(ch.new)}`).join("; ")}
                      </Row>
                    ))}
                    {m.activities.removed.map((a, ai) => (
                      <Row key={`ar-${mi}-${ai}`} k={`v${mi}-ar-${ai}`} icon={<Minus className="w-3 h-3" />} color="text-red-600">
                        − activity <strong>{a.name}</strong> · {fmtUSD(a.amount)}
                      </Row>
                    ))}
                  </div>
                ))}
              </Section>
            )}

            {(diff.pass_throughs.added.length + diff.pass_throughs.removed.length + diff.pass_throughs.changed.length) > 0 && (
              <Section title="Pass-Throughs" count={diff.pass_throughs.added.length + diff.pass_throughs.removed.length + diff.pass_throughs.changed.length}>
                {diff.pass_throughs.added.map((p, idx) => (
                  <Row key={`pt-add-${idx}`} k={`pt-add-${idx}`} icon={<Plus className="w-3 h-3" />} color="text-emerald-600">
                    Add <strong>{p.name}</strong> · {fmtUSD(p.amount)}
                  </Row>
                ))}
                {diff.pass_throughs.changed.map((c, idx) => (
                  <Row key={`pt-chg-${idx}`} k={`pt-chg-${idx}`} icon={<Pencil className="w-3 h-3" />} color="text-amber-600">
                    Update <strong>{c.existing.name}</strong>: {c.changes.map((ch) => `${ch.field} ${String(ch.old)} → ${String(ch.new)}`).join("; ")}
                  </Row>
                ))}
                {diff.pass_throughs.removed.map((p, idx) => (
                  <Row key={`pt-rem-${idx}`} k={`pt-rem-${idx}`} icon={<Minus className="w-3 h-3" />} color="text-red-600">
                    Remove <strong>{p.name}</strong> · {fmtUSD(p.amount)}
                  </Row>
                ))}
              </Section>
            )}

            {diff.summary.total_changes === 0 && (
              <div className="text-sm text-slate-500 bg-emerald-50 border border-emerald-200 rounded p-4 text-center">
                No differences detected. The saved CTA matches the document.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === "diff" && (
            <>
              <Button variant="ghost" onClick={reset}>Re-upload</Button>
              <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
              <Button
                onClick={apply}
                disabled={busy || diff?.summary?.total_changes === 0}
                className="bg-[#0A4C95] hover:bg-[#083d79]"
                data-testid="apply-diff-btn"
              >
                {busy ? "Applying..." : "Apply Selected"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryStat({ label, v, highlight }) {
  return (
    <div className={`border rounded p-2 ${highlight ? "border-[#0A4C95] bg-blue-50" : "border-slate-200 bg-slate-50"}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="font-mono-fin text-lg font-bold mt-1">{v}</div>
    </div>
  );
}
